import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Officer Slice
 * Redux state management for officer-related data.
 *
 * @author Civilhealth Development Team
 */

// Types
interface Officer {
  id: number;
  name: string;
  email: string;
  phone: string;
  designation: string;
  department: string;
  officerType: string;
  status: string;
  createdAt: string;
}

interface OfficerState {
  officers: Officer[];
  currentOfficer: Officer | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: OfficerState = {
  officers: [],
  currentOfficer: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchOfficers = createAsyncThunk(
  'officer/fetchOfficers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/officers');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch officers');
    }
  }
);

export const fetchOfficerById = createAsyncThunk(
  'officer/fetchOfficerById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/officers/${id}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch officer');
    }
  }
);

export const fetchOfficerProfile = createAsyncThunk(
  'officer/fetchOfficerProfile',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/officers/user/${userId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch officer profile');
    }
  }
);

// Slice
const officerSlice = createSlice({
  name: 'officer',
  initialState,
  reducers: {
    clearOfficerError: (state) => {
      state.error = null;
    },
    clearCurrentOfficer: (state) => {
      state.currentOfficer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfficers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOfficers.fulfilled, (state, action) => {
        state.loading = false;
        state.officers = action.payload;
      })
      .addCase(fetchOfficers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOfficerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOfficerById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOfficer = action.payload;
      })
      .addCase(fetchOfficerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOfficerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOfficerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOfficer = action.payload;
      })
      .addCase(fetchOfficerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearOfficerError, clearCurrentOfficer } = officerSlice.actions;
export default officerSlice.reducer;